<script setup lang="ts">
// Import libraries
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { syncRef, useThrottleFn } from '@vueuse/core';
import { useRoute } from 'vue-router';

// Import components
import BrandLink from '~/components/site-header/BrandLink.vue';
import MenuButton from '~/components/site-header/MenuButton.vue';
import MenuOverlay from '~/components/site-header/MenuOverlay.vue';

// Settings
const SCROLL_THRESHOLD = 150;
const SCROLL_THROTTLE_TIME = 100;

// Application wide state for the header intersection
const headerIsIntersecting = useState<boolean>(
  'headerIsIntersecting',
  () => false,
);

const route = useRoute();

// Initialize refs
const isLoaded = ref(false);
const isNavOpen = ref(false);
const isHeaderHidden = ref(false);
const isOnBackground = ref(false);
const lastScrollY = ref(0);

// Sync the headerIsIntersecting state with the isOnBackground ref
syncRef(headerIsIntersecting, isOnBackground);

// Handle the header states
const openNav = () => {
  isNavOpen.value = true;
};

const closeNav = () => {
  isNavOpen.value = false;
};

const showHeader = () => {
  isHeaderHidden.value = false;
};

const hideHeader = () => {
  isHeaderHidden.value = true;
};

// Handle the scroll event
const handleScroll = useThrottleFn(() => {
  const currentScrollY = window.scrollY;
  const scrollDirection: 'up' | 'down' =
    currentScrollY > lastScrollY.value ? 'down' : 'up';

  if (scrollDirection === 'down' && currentScrollY > SCROLL_THRESHOLD) {
    hideHeader();
  } else if (scrollDirection === 'up') {
    showHeader();
  }

  lastScrollY.value = currentScrollY;
}, SCROLL_THROTTLE_TIME);

// Reset the header intersection state when the route changes
watch(
  () => route.path,
  () => {
    headerIsIntersecting.value = false;
  },
);

onMounted(() => {
  isLoaded.value = true;
  if (typeof window !== 'undefined') {
    lastScrollY.value = window.scrollY;
    window.addEventListener('scroll', handleScroll, { passive: true });
  }
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', handleScroll);
  }
});
</script>

<template>
  <header
    class="site-header"
    :class="{ 'is-hidden': isHeaderHidden, 'is-not-loaded': !isLoaded }"
    role="banner"
  >
    <BrandLink :is-on-background="isOnBackground" />
    <MenuButton :is-on-background="isOnBackground" @click="openNav" />
    <MenuOverlay :is-open="isNavOpen" @close-nav="closeNav" />
  </header>
</template>

<style scoped lang="scss">
.site-header {
  z-index: 100;
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  transform: translateY(0);
  transition: transform 0.5s var(--base-transition-easing);

  &.is-not-loaded,
  &.is-hidden {
    transform: translateY(-100%);
  }

  @media (--vs) {
    padding: 1.25rem;
  }

  @media (--vl) {
    padding: 3.5rem 5rem;
  }
}
</style>
